import GitHubIcon from "@mui/icons-material/GitHub";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import YouTubeIcon from "@mui/icons-material/YouTube";
import { Alert, AlertTitle, Container, ThemeProvider } from "@mui/material";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import * as React from "react";

import CustomCard from "./Modules/CustomCard";
import ExtraInfoModal from "./Modules/ExtraInfoModal";
import FeaturedCard from "./Modules/FeaturedCard";
import theme from "./Modules/theme";
import debounce from "./Utils/Debounce";

const secondaryTextColor = "rgba(255, 255, 255, 0.7)";


function CertificationsPanel() {
    const certifications = [
        {
            title: "CompTIA A+",
            issuer: "CompTIA",
            level: "Expert",
            image: "/assets/badges/comptiaAPlus.png",
            url: "https://www.credly.com/earner/earned/share/b67e8e8f-c60d-4216-8e3e-33a947c448234"
        },
        {
            title: "MS Office Specialist: Excel Expert",
            issuer: "Microsoft",
            level: "Expert",
            image: "/assets/badges/msExcelExpert.png",
            url: "https://www.credly.com/badges/ae09ba32-e365-40a2-9889-e3ab954e3427/public_url"
        },
        {
            title: "MS Office Specialist: Associate",
            issuer: "Microsoft",
            level: "Intermediate",
            image: "/assets/badges/msSpecialist.png",
            url: "https://www.credly.com/badges/35354489-41ef-4a91-aa88-47129442b20d/public_url"
        },
        {
            title: "PCAP - Certified Associate Python Programmer",
            issuer: "Python Institute",
            level: "Advanced",
            image: "/assets/badges/pcapBadge.png",
            url: "https://www.credly.com/badges/7d9d1b96-b3db-4506-b0f8-a071b7b2685f/public_url"
        },
        {
            title: "CompTIA IT Fundamentals+",
            issuer: "CompTIA",
            level: "Beginner",
            image: "/assets/badges/comptiaITF.png",
            url: "https://www.credly.com/badges/dfb3f133-53c3-4c79-ae68-8228cafa1634"
        },
        {
            title: "IT Specialist - Python",
            issuer: "Certiport",
            level: "Intermediate",
            image: "/assets/badges/itsBadge.png",
            url: "https://www.credly.com/badges/ead63567-73ee-4c43-bd5e-33e6f8f76ea4/public_url"
        },
        {
            title: "Introduction to Programming Using Python",
            issuer: "Microsoft",
            level: "Beginner",
            image: "/assets/badges/mtaPython.png",
            url: "https://www.credly.com/badges/39dffc74-8554-48c7-9675-9bced88ad136/public_url"
        }
    ];

    const getLevelColor = (level: string) => {
        switch (level) {
        case "Beginner": return "#4CAF50";
        case "Intermediate": return "#FF9800";
        case "Advanced": return "#2196F3";
        case "Expert": return "#9C27B0";
        default: return "#2580e1";
        }
    };

    return (
        <Box
            sx={{
                background: "linear-gradient(135deg, #2D3748 0%, #1A202C 100%)",
                borderRadius: 3,
                p: 4,
                boxShadow: "0 20px 40px rgba(0,0,0,0.3)",
                border: "1px solid rgba(255,255,255,0.1)"
            }}
        >
            <Typography
                variant="h4"
                sx={{
                    color: "white",
                    fontWeight: "bold",
                    mb: 3,
                    textAlign: "center"
                }}
            >
                Professional Certifications
            </Typography>

            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: {
                        xs: "repeat(2, 1fr)",
                        sm: "repeat(3, 1fr)",
                        md: "repeat(4, 1fr)"
                    },
                    gap: 4,
                    mb: 4
                }}
            >
                {certifications.map((cert, index) => (
                    <Box
                        key={index}
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            cursor: "pointer",
                            transition: "transform 0.2s ease",
                            "&:hover": {
                                transform: "scale(1.05)"
                            }
                        }}
                        onClick={() => window.open(cert.url, "_blank")}
                    >
                        <Box
                            sx={{
                                width: 120,
                                height: 120,
                                borderRadius: 3,
                                background: "rgba(255,255,255,0.1)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                mb: 2,
                                position: "relative",
                                overflow: "hidden"
                            }}
                        >
                            <img
                                src={cert.image}
                                alt={cert.title}
                                style={{
                                    maxWidth: "90px",
                                    maxHeight: "90px",
                                    objectFit: "contain"
                                }}
                            />
                        </Box>
                        <Box
                            sx={{
                                width: "100%",
                                textAlign: "center"
                            }}
                        >
                            <Typography
                                variant="body2"
                                sx={{
                                    color: "white",
                                    fontWeight: "bold",
                                    fontSize: "0.9rem",
                                    lineHeight: 1.3,
                                    display: "block",
                                    mb: 1,
                                    minHeight: "2.6rem"
                                }}
                            >
                                {cert.title}
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: "#2580e1",
                                    fontSize: "0.8rem",
                                    display: "block",
                                    mb: 1,
                                    fontWeight: "500"
                                }}
                            >
                                {cert.issuer}
                            </Typography>
                            <Box
                                sx={{
                                    backgroundColor: getLevelColor(cert.level),
                                    color: "white",
                                    px: 2,
                                    py: 0.75,
                                    borderRadius: 2,
                                    fontSize: "0.75rem",
                                    fontWeight: "bold",
                                    textTransform: "uppercase",
                                    display: "inline-block"
                                }}
                            >
                                {cert.level}
                            </Box>
                        </Box>
                    </Box>
                ))}
            </Box>

            <Box
                sx={{
                    textAlign: "center",
                    pt: 2,
                    borderTop: "1px solid rgba(255,255,255,0.1)"
                }}
            >
                <Typography
                    variant="body2"
                    sx={{
                        color: "rgba(255,255,255,0.7)",
                        mb: 1
                    }}
                >
                    Click any certification to view credentials
                </Typography>
                <Box sx={{ display: "flex", justifyContent: "center", gap: 2, flexWrap: "wrap" }}>
                    <Typography variant="caption" sx={{ color: "#4CAF50" }}>● Beginner</Typography>
                    <Typography variant="caption" sx={{ color: "#FF9800" }}>● Intermediate</Typography>
                    <Typography variant="caption" sx={{ color: "#2196F3" }}>● Advanced</Typography>
                    <Typography variant="caption" sx={{ color: "#9C27B0" }}>● Expert</Typography>
                </Box>
            </Box>
        </Box>
    );
} function ScrollableCards(props: { children: React.ReactNode }) {
    return (
        <Box
            sx={{
                display: "flex",
                overflowX: "auto",
                overflowY: "hidden",
                gap: 1,
                paddingBottom: 1,
                "&::-webkit-scrollbar": {
                    height: 8,
                },
                "&::-webkit-scrollbar-track": {
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                    borderRadius: 4,
                },
                "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "#2580e1",
                    borderRadius: 4,
                    "&:hover": {
                        backgroundColor: "#1976d2",
                    },
                },
            }}
        >
            {props.children}
        </Box>
    );
}
function TopDiv() {
    // 288 is the height of the pfp image, so we use it as default height to use; most people will view this on a large screened device where the 288 value is what is used later too
    const [pfpHeight, setPfpHeight] = React.useState<number | undefined>(288);
    const pfpRef = React.useRef<HTMLImageElement>(null);

    // deals with window resizing: https://www.pluralsight.com/guides/re-render-react-component-on-window-resize
    const [, setDimensions] = React.useState(window.innerWidth)
    React.useEffect(() => {
        const debouncedHandleResize = debounce(() => {
            setDimensions(window.innerWidth)
            setPfpHeight(pfpRef.current?.clientHeight);
        }, 50)

        window.addEventListener("resize", debouncedHandleResize)

        return () => {
            window.removeEventListener("resize", debouncedHandleResize)

        }
    })
    const links = { linkedin: "https://www.linkedin.com/in/iksteiger", github: "https://github.com/isteiger", youtube: "https://www.youtube.com/channel/UCX8m1Mtl75RQp3qlRxK1FXg" }

    return (
        <div style={{ paddingBottom: "1rem" }}>
            <div className="is-no-top-shadow is-bg-dark topbg" style={{ paddingTop: "20rem" }} >
                {/* appears on smaller screens */}
                <Box sx={{ boxShadow: 3 }} className="frost">
                    <Box style={{ width: "100%", flexDirection: "column", textAlign: "center" }} className="transparent appearOnSmallScreens">
                        <Container maxWidth="xl" style={{ alignItems: "end", height: "100%", padding: "0" }}>
                            <IconButton className="smallMarginRight" onClick={() => {
                                window.open(links.github, "_blank");
                            }}>
                                <GitHubIcon htmlColor={secondaryTextColor} fontSize="large" />
                            </IconButton>
                            <IconButton className="smallMarginRight" onClick={() => {
                                window.open(links.linkedin, "_blank");
                            }}>
                                <LinkedInIcon htmlColor={secondaryTextColor} fontSize="large" />
                            </IconButton>
                            <IconButton className="smallMarginRight" onClick={() => {
                                window.open(links.youtube, "_blank");
                            }}>
                                <YouTubeIcon htmlColor={secondaryTextColor} fontSize="large" />
                            </IconButton>
                        </Container>

                        <Container maxWidth="xl" style={{ alignItems: "end", padding: "0" }}>
                            <Typography variant="h3" component="div" color="white" style={{ display: "block", flexWrap: "wrap" }} className="centerUntilThing sectionHeadingText">
                                <b>Ian Steiger</b>
                            </Typography>
                        </Container>
                        <Container maxWidth="xl" style={{ alignItems: "end", padding: "0" }}>
                            <Typography variant="h6" component="div" style={{ display: "block", flexWrap: "wrap", color: secondaryTextColor }} className="centerUntilThing sectionHeadingText">
                                Computer Science Student @ UNC Charlotte
                            </Typography>
                        </Container>
                    </Box>
                    <Box style={{ height: `calc(${pfpHeight}px - min(20vw, 100px))`, width: "100%", paddingBottom: ".5rem", display: "flex", flexDirection: "column" }} className="transparent">

                        <Container maxWidth="xl" style={{ alignItems: "end", height: "100%", padding: "0" }} className="hideForNow">
                            <div style={{ width: "calc(24px + 0.5rem + 288px + 8px)", maxWidth: "50vw", display: "inline-block" }}></div>
                            <IconButton className="smallMarginRight" onClick={() => {
                                window.open(links.github, "_blank");
                            }}>
                                <GitHubIcon htmlColor={secondaryTextColor} fontSize="large" />
                            </IconButton>
                            <IconButton className="smallMarginRight" onClick={() => {
                                window.open(links.linkedin, "_blank");
                            }}>
                                <LinkedInIcon htmlColor={secondaryTextColor} fontSize="large" />
                            </IconButton>
                            <IconButton className="smallMarginRight" onClick={() => {
                                window.open(links.youtube, "_blank");
                            }}>
                                <YouTubeIcon htmlColor={secondaryTextColor} fontSize="large" />
                            </IconButton>
                        </Container>

                        <Container maxWidth="xl" style={{ alignItems: "end", padding: "0" }} className="hideForNow">
                            <div style={{ width: "calc(24px + 0.5rem + 288px + 8px)", maxWidth: "70vw", display: "inline-block" }}></div>
                            <Typography variant="h3" component="div" color="white" style={{ display: "block", flexWrap: "wrap" }} className="centerUntilThing sectionHeadingText">
                                <b>Ian Steiger</b>
                            </Typography>
                        </Container>
                        <Container maxWidth="xl" style={{ alignItems: "end", padding: "0" }} className="hideForNow">
                            <div style={{ width: "calc(24px + 0.5rem + 288px + 8px)", maxWidth: "70vw", display: "inline-block" }}></div>
                            <Typography variant="h6" component="div" style={{ display: "block", flexWrap: "wrap", color: secondaryTextColor }} className="centerUntilThing sectionHeadingText">
                                Computer Science Student @ UNC Charlotte
                            </Typography>
                        </Container>
                    </Box>
                </Box>
            </div>

            <Container maxWidth="xl" style={{ position: "relative" }}>
                <Box sx={{ boxShadow: 5 }} className="is-inline-flex is-circle is-pfp">
                    <picture style={{ display: "contents" }}>
                        <source srcSet="/assets/pfp.webp" type="image/webp" />
                        <source srcSet="/assets/pfp.jpg" type="image/jpeg" />
                        <img src="/assets/pfp.jpg" alt="Me" className="is-circle" ref={pfpRef} onLoad={() => { setPfpHeight(pfpRef.current?.clientHeight); }} />
                    </picture>
                </Box>
            </Container>
        </div>
    )
}
function App() {
    return (
        <ThemeProvider theme={theme}>
            <div className="App">
                <Box sx={{ flexGrow: 1 }} className="is-bg-dark">
                    {/*<ResponsiveAppBar />*/}

                </Box>
                <TopDiv />
                <Container maxWidth="xl" >
                    <div className="siteBody">
                        <Alert severity="info">
                            <AlertTitle>Please excuse the mess</AlertTitle>
                            This site is a work in progress.
                        </Alert>
                        <br />
                        <FeaturedCard />
                        <CertificationsPanel />
                    </div>
                    <Typography variant="h4" gutterBottom component="div" color="white" style={{ display: "flex", flexWrap: "wrap" }} className="centerUntilThing">
                        Projects I've Worked On
                    </Typography>
                    <ScrollableCards>
                        <CustomCard title="PinkertonMC" image="/assets/pmcLogo" alt="PinkertonMC Logo" imgFallback="png" disableShadow={true} url="https://docs.microsoft.com/en-us/learn/certifications/mta-introduction-to-programming-using-python/" body="A Minecraft server network that aimed to set itself apart." />
                        <CustomCard title="Wake Students" image="/assets/wakeStudents" imgFallback="png" alt="Wake Students Logo" url="https://www.comptia.org/certifications/it-fundamentals" body="Website for unblocked games at school." />
                    </ScrollableCards>
                    <ExtraInfoModal image="/assets/wakeStudents" imgFallback="png" alt="Test Dialog" body="" url="" />
                </Container>
            </div >
        </ThemeProvider >
    );
}

export default App;
